// A floating, movable, resizable window rect (like Zoom's): drag by the title bar, resize from any edge or corner.
// The rect is kept inside the browser window and remembered for next time.
import { useCallback, useEffect, useRef, useState } from 'react';

const KEY = 'strata.meeting.window';
export const MIN_W = 520;
export const MIN_H = 380;
const GRAB = 120;  // px of the title bar that must stay on screen

const view = () => ({ w: window.innerWidth, h: window.innerHeight });

function fit(r) {
  const v = view();
  const w = Math.min(Math.max(r.w, Math.min(MIN_W, v.w)), v.w);
  const h = Math.min(Math.max(r.h, Math.min(MIN_H, v.h)), v.h);
  const x = Math.min(Math.max(r.x, GRAB - w), v.w - GRAB);
  const y = Math.min(Math.max(r.y, 0), v.h - 48);
  return { x, y, w, h };
}

function initial() {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (saved && saved.w && saved.h) return fit(saved);
  } catch { /* none */ }
  const v = view();
  const w = Math.min(1100, v.w - 48);
  const h = Math.min(720, v.h - 48);
  return fit({ w, h, x: (v.w - w) / 2, y: (v.h - h) / 2 });
}

// Edges / corners and how each moves the rect.
export const EDGES = {
  n: { cursor: 'ns-resize', cls: 'left-3 right-3 top-0 h-1.5' },
  s: { cursor: 'ns-resize', cls: 'left-3 right-3 bottom-0 h-1.5' },
  e: { cursor: 'ew-resize', cls: 'top-3 bottom-3 right-0 w-1.5' },
  w: { cursor: 'ew-resize', cls: 'top-3 bottom-3 left-0 w-1.5' },
  ne: { cursor: 'nesw-resize', cls: 'right-0 top-0 h-3 w-3' },
  nw: { cursor: 'nwse-resize', cls: 'left-0 top-0 h-3 w-3' },
  se: { cursor: 'nwse-resize', cls: 'right-0 bottom-0 h-4 w-4' },
  sw: { cursor: 'nesw-resize', cls: 'left-0 bottom-0 h-3 w-3' },
};

function resized(start, edge, dx, dy) {
  let { x, y, w, h } = start;
  const v = view();
  if (edge.includes('e')) w = Math.min(Math.max(start.w + dx, MIN_W), v.w - x);
  if (edge.includes('s')) h = Math.min(Math.max(start.h + dy, MIN_H), v.h - y);
  if (edge.includes('w')) { const nx = Math.max(Math.min(start.x + dx, start.x + start.w - MIN_W), 0); w = start.w + (start.x - nx); x = nx; }
  if (edge.includes('n')) { const ny = Math.max(Math.min(start.y + dy, start.y + start.h - MIN_H), 0); h = start.h + (start.y - ny); y = ny; }
  return { x, y, w, h };
}

export function useFloatingWindow() {
  const [rect, setRect] = useState(initial);
  const [moving, setMoving] = useState(null);  // 'drag' | edge while a gesture runs
  const gesture = useRef(null);

  useEffect(() => {
    const onResize = () => setRect((r) => fit(r));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const begin = useCallback((kind) => (e) => {
    if (e.button !== 0) return;
    if (kind === 'drag' && e.target.closest('button, input, select, textarea, a, [data-no-drag]')) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    gesture.current = { kind, sx: e.clientX, sy: e.clientY, start: rect };
    setMoving(kind);
  }, [rect]);

  const move = useCallback((e) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.sx;
    const dy = e.clientY - g.sy;
    setRect(g.kind === 'drag' ? fit({ ...g.start, x: g.start.x + dx, y: g.start.y + dy }) : resized(g.start, g.kind, dx, dy));
  }, []);

  const end = useCallback(() => {
    if (!gesture.current) return;
    gesture.current = null;
    setMoving(null);
    setRect((r) => { try { localStorage.setItem(KEY, JSON.stringify(r)); } catch { /* private mode */ } return r; });
  }, []);

  const handlers = (kind) => ({ onPointerDown: begin(kind), onPointerMove: move, onPointerUp: end, onPointerCancel: end });
  return { rect, moving, dragProps: handlers('drag'), edgeProps: (edge) => handlers(edge) };
}
